import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator } from "@phosphor-icons/react";
import { usesWetSavorySafetyTools } from "@/lib/product-types";
import { measuredYield } from "@/lib/measured-yield";

const LB_TO_G = 453.592;
const GAL_TO_ML = 3785.0;

function LabelField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-stone-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Metric({ label, value, sub, highlight, testId }) {
  return (
    <div
      className={`rounded-md border px-3 py-2 ${highlight ? "bg-brand-50 border-brand-300" : "bg-stone-50 border-stone-200"}`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`font-mono text-lg font-semibold ${highlight ? "text-brand-800" : "text-stone-800"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function SnackCalculator() {
  const [snackTypes, setSnackTypes] = useState([]);
  const [snackKey, setSnackKey] = useState("popcorn");
  const [snackUnit, setSnackUnit] = useState("lb");
  const [snackBaseLb, setSnackBaseLb] = useState(5);
  const [snackResult, setSnackResult] = useState(null);

  useEffect(() => {
    api.get("/application/snack-types")
      .then((r) => setSnackTypes(r.data.snack_types || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!snackKey || !snackBaseLb) { setSnackResult(null); return; }
    const baseG = snackUnit === "lb" ? Number(snackBaseLb) * LB_TO_G : Number(snackBaseLb);
    const t = setTimeout(() => {
      api.post("/application/snack", { snack_type: snackKey, base_weight_g: baseG })
        .then((r) => setSnackResult(r.data))
        .catch(() => setSnackResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [snackKey, snackBaseLb, snackUnit]);

  const handleUnitChange = (v) => {
    if (v === "g" && snackUnit === "lb") setSnackBaseLb((Number(snackBaseLb) * LB_TO_G).toFixed(0));
    if (v === "lb" && snackUnit === "g") setSnackBaseLb((Number(snackBaseLb) / LB_TO_G).toFixed(2));
    setSnackUnit(v);
  };

  return (
    <Card className="p-5 border-stone-200" data-testid="snack-application-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-brand-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Snack Application · slurry &amp; topical</div>
          <h3 className="font-semibold mt-0.5">Chips, popcorn, pretzels &amp; pork rinds</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <LabelField label="Snack base">
          <Select value={snackKey} onValueChange={setSnackKey}>
            <SelectTrigger data-testid="snack-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {snackTypes.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabelField>
        <LabelField label={`Base weight (${snackUnit})`}>
          <div className="flex gap-2">
            <Input type="number" step={snackUnit === "lb" ? "0.1" : "1"} min="0.1"
              value={snackBaseLb}
              onChange={(e) => setSnackBaseLb(e.target.value)}
              data-testid="snack-base-weight" className="flex-1" />
            <Select value={snackUnit} onValueChange={handleUnitChange}>
              <SelectTrigger className="w-[80px]" data-testid="snack-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lb">lb</SelectItem>
                <SelectItem value="g">g</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </LabelField>
      </div>
      {snackResult && (
        <>
          <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="snack-result">
            <Metric label={`Light · ${snackResult.rate_low_pct}%`}
              value={`${snackResult.low_g}g`} sub={`${snackResult.low_oz}oz seasoning`} testId="snack-low" />
            <Metric label={`Recommended · ${snackResult.rate_mid_pct}%`}
              value={`${snackResult.mid_g}g`} sub={`${snackResult.mid_oz}oz seasoning`} highlight testId="snack-mid" />
            <Metric label={`Heavy · ${snackResult.rate_high_pct}%`}
              value={`${snackResult.high_g}g`} sub={`${snackResult.high_oz}oz seasoning`} testId="snack-high" />
          </div>
          {snackResult.slurry && (
            <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm" data-testid="slurry-breakdown">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs uppercase tracking-wider text-amber-800 font-bold">Slurry formulation</span>
                <span className="text-[10px] text-amber-700">{snackResult.slurry_ratio}</span>
              </div>
              <div className="text-stone-700">
                At <b>{snackResult.rate_mid_pct}% mid-rate</b> for {snackBaseLb} {snackUnit} base:
              </div>
              <div className="mt-1 grid grid-cols-2 gap-3 text-[12px]">
                <div>Seasoning: <b>{snackResult.mid_g} g</b> ({snackResult.mid_oz} oz)</div>
                <div>Carrier oil: <b>{snackResult.slurry.mid_oil_g} g</b></div>
              </div>
            </div>
          )}
          <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
            <b className="text-stone-700">Method:</b> {snackResult.method}
          </div>
          <div className="mt-2 text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
            <b>Notes:</b> {snackResult.notes}
          </div>
        </>
      )}
    </Card>
  );
}

function MarinadeCalculator() {
  const [marinadeProteins, setMarinadeProteins] = useState([]);
  const [marProteinKey, setMarProteinKey] = useState("chicken_breast");
  const [marUnit, setMarUnit] = useState("lb");
  const [marRawLb, setMarRawLb] = useState(10);
  const [marIntensity, setMarIntensity] = useState("medium");
  const [marResult, setMarResult] = useState(null);
  const [cookedWeight, setCookedWeight] = useState("");
  const [preCookWeight, setPreCookWeight] = useState("");
  const measured = measuredYield(marRawLb, cookedWeight, preCookWeight);
  const invalidWeight = (v) => v !== "" && (!Number.isFinite(Number(v)) || Number(v) <= 0);

  useEffect(() => {
    api.get("/application/marinade-proteins")
      .then((r) => setMarinadeProteins(r.data.proteins || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!marProteinKey || !marRawLb || !marIntensity) { setMarResult(null); return; }
    const rawG = marUnit === "lb" ? Number(marRawLb) * LB_TO_G : Number(marRawLb);
    const t = setTimeout(() => {
      api.post("/application/marinade", { protein: marProteinKey, raw_weight_g: rawG, marinade_intensity: marIntensity })
        .then((r) => setMarResult(r.data))
        .catch(() => setMarResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [marProteinKey, marRawLb, marIntensity, marUnit]);

  const handleUnitChange = (v) => {
    const factor = v === marUnit ? 1 : v === "g" ? LB_TO_G : 1 / LB_TO_G;
    const convert = (value) => value === "" ? "" : String(Number((Number(value) * factor).toPrecision(12)));
    setMarRawLb(convert(marRawLb));
    setCookedWeight(convert(cookedWeight));
    setPreCookWeight(convert(preCookWeight));
    setMarUnit(v);
  };

  return (
    <Card className="p-5 border-stone-200" data-testid="marinade-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-brand-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Marinade · yield &amp; purge</div>
          <h3 className="font-semibold mt-0.5">Meat &amp; protein marinade analysis</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <LabelField label="Protein">
          <Select value={marProteinKey} onValueChange={setMarProteinKey}>
            <SelectTrigger data-testid="marinade-protein-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {marinadeProteins.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabelField>
        <LabelField label={`Raw weight (${marUnit})`}>
          <div className="flex gap-2">
            <Input type="number" step={marUnit === "lb" ? "0.5" : "10"} min="0.5"
              value={marRawLb}
              onChange={(e) => setMarRawLb(e.target.value)}
              data-testid="marinade-raw-weight" className="flex-1" />
            <Select value={marUnit} onValueChange={handleUnitChange}>
              <SelectTrigger className="w-[80px]" data-testid="marinade-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lb">lb</SelectItem>
                <SelectItem value="g">g</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </LabelField>
        <LabelField label="Intensity">
          <Select value={marIntensity} onValueChange={setMarIntensity}>
            <SelectTrigger data-testid="marinade-intensity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="heavy">Heavy</SelectItem>
            </SelectContent>
          </Select>
        </LabelField>
      </div>
      <section className="mt-4 p-4 rounded-md bg-brand-50 border border-brand-200" aria-label="Measured cooking yield">
        <h4 className="font-semibold text-brand-800">Measured cooking yield</h4>
        <p className="text-xs text-stone-600 mt-1 mb-3">Use net product weights from the same batch, excluding containers. Raw weight above is before marination.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="mar-post-cook">Post-cook weight ({marUnit})</Label>
            <Input id="mar-post-cook" type="number" min="0" step="any" value={cookedWeight}
              placeholder="Enter measured cooked weight" data-testid="mar-post-cook-weight"
              aria-invalid={invalidWeight(cookedWeight)}
              onChange={(e) => setCookedWeight(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="mar-pre-cook">Pre-cook weight after marination ({marUnit}, optional)</Label>
            <Input id="mar-pre-cook" type="number" min="0" step="any" value={preCookWeight}
              placeholder="Weigh just before cooking" data-testid="mar-pre-cook-weight"
              aria-invalid={invalidWeight(preCookWeight)}
              onChange={(e) => setPreCookWeight(e.target.value)} />
          </div>
        </div>
        {(invalidWeight(cookedWeight) || invalidWeight(preCookWeight) || invalidWeight(marRawLb)) &&
          <p role="alert" className="text-sm text-red-700 mt-2">Weights must be finite numbers greater than zero.</p>}
        {measured && (
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <Metric label="Measured yield · raw basis" value={`${measured.rawPct.toFixed(1)}%`}
              sub="Post-cook ÷ raw weight × 100" highlight testId="mar-measured-raw-yield" />
            <Metric label="Measured cooking yield" value={measured.cookPct === null ? "—" : `${measured.cookPct.toFixed(1)}%`}
              sub={measured.cookPct === null ? "Enter measured pre-cook weight" : "Post-cook ÷ pre-cook weight × 100"} highlight testId="mar-measured-cook-yield" />
            <Metric label={measured.loss < 0 ? "Measured cooking weight gain" : "Measured cooking loss"}
              value={measured.loss === null ? "—" : `${Math.abs(measured.loss).toFixed(2)} ${marUnit}`}
              sub="Based on measured pre-cook weight" testId="mar-measured-loss" />
          </div>
        )}
        {measured?.cookPct > 100 && <p className="mt-2 text-xs text-amber-800">Post-cook weight exceeds pre-cook weight. Check units, tare and any added liquid; the measured result has not been capped.</p>}
        <p className="mt-3 text-xs text-stone-600">These calculator entries are temporary, not saved with the formula. Print or download this section to keep the measurements. Estimates below remain separate.</p>
      </section>
      {marResult && (
        <>
          <h4 className="mt-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Reference estimates · not measured results</h4>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="marinade-result">
            <Metric label="Marinade pickup"
              value={`${marResult.marinade_pickup_pct}%`} sub={`${marResult.marinade_g} g`} testId="mar-pickup" />
            <Metric label="Cook yield (raw)"
              value={`${marResult.cook_yield_unmarinated_pct}%`} sub={`${marResult.cooked_yield_unmarinated_g} g cooked`} testId="mar-yield-raw" />
            <Metric label="Cook yield (marinated)"
              value={`${marResult.cook_yield_marinated_pct}%`} sub={`${marResult.cooked_yield_marinated_g} g cooked`} highlight testId="mar-yield-marinated" />
            <Metric label="Yield lift"
              value={`+${marResult.yield_lift_pct}%`} sub="vs unmarinated" testId="mar-yield-lift" />
          </div>

          <div className="mt-3 p-3 rounded-md bg-brand-50 border border-brand-200 text-sm" data-testid="dollar-lift-note">
            <span className="text-xs uppercase tracking-wider text-brand-800 font-bold">Margin lift</span>
            <div className="mt-1 text-stone-700 text-[13px] leading-relaxed">
              {marResult.cook_yield_dollar_lift_note}
            </div>
          </div>

          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-[12px]">
            <div className="p-3 rounded-md bg-stone-50 border border-stone-200">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Refrigerated purge</div>
              <div className="text-stone-800"><b>{marResult.purge_pct_per_week_refrig}%</b> per week</div>
              <div className="text-stone-600 mt-0.5">≈ {marResult.purge_g_per_week} g / week of liquid loss</div>
            </div>
            <div className="p-3 rounded-md bg-stone-50 border border-stone-200">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Salt recommendation</div>
              <div className="text-stone-800"><b>{marResult.salt_recommendation_pct}%</b> of finished weight</div>
              <div className="text-stone-600 mt-0.5">≈ {marResult.salt_recommendation_g} g salt · {marResult.salt_recommendation_mg_sodium} mg sodium</div>
            </div>
          </div>

          <div className="mt-3 text-[11px] text-stone-600 leading-relaxed bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <b className="text-amber-800">Phosphate guidance:</b> {marResult.phosphate_recommendation}
          </div>

          <div className="mt-2 text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
            <b>Notes:</b> {marResult.notes}
          </div>
        </>
      )}
    </Card>
  );
}

function BeverageBatchCalculator() {
  const [finalVolumeL, setFinalVolumeL] = useState(100);
  const [inputBrix, setInputBrix] = useState(65);
  const [targetBrix, setTargetBrix] = useState(10);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!finalVolumeL || !inputBrix || !targetBrix) {
      setResult(null);
      setError("");
      return;
    }
    const t = setTimeout(() => {
      api.post("/application/beverage-batch", {
        final_volume_l: Number(finalVolumeL),
        input_brix: Number(inputBrix),
        target_brix: Number(targetBrix),
      })
        .then((r) => {
          setResult(r.data);
          setError("");
        })
        .catch((err) => {
          setResult(null);
          setError(err?.response?.data?.detail || "Check the Brix and batch-volume inputs.");
        });
    }, 250);
    return () => clearTimeout(t);
  }, [finalVolumeL, inputBrix, targetBrix]);

  return (
    <Card className="p-5 border-fuchsia-200" data-testid="beverage-brix-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-fuchsia-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Beverage · Brix &amp; dilution</div>
          <h3 className="font-semibold mt-0.5">Concentrate-to-finished batch estimate</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <LabelField label="Final batch volume (L)">
          <Input
            type="number"
            min="0.001"
            step="1"
            value={finalVolumeL}
            onChange={(e) => setFinalVolumeL(e.target.value)}
            data-testid="beverage-final-volume"
          />
        </LabelField>
        <LabelField label="Input / concentrate °Brix">
          <Input
            type="number"
            min="0.01"
            max="100"
            step="0.1"
            value={inputBrix}
            onChange={(e) => setInputBrix(e.target.value)}
            data-testid="beverage-input-brix"
          />
        </LabelField>
        <LabelField label="Target finished °Brix">
          <Input
            type="number"
            min="0.01"
            max="100"
            step="0.1"
            value={targetBrix}
            onChange={(e) => setTargetBrix(e.target.value)}
            data-testid="beverage-target-brix"
          />
        </LabelField>
      </div>
      {error && (
        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
          {error}
        </div>
      )}
      {result && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="beverage-result">
            <Metric
              label="Concentrate"
              value={`${result.concentrate_kg} kg`}
              sub={`at ${result.input_brix} °Brix`}
              highlight
              testId="beverage-concentrate"
            />
            <Metric
              label="Dilution water"
              value={`${result.dilution_water_kg} kg`}
              sub="estimated addition"
              testId="beverage-water"
            />
            <Metric
              label="Water : concentrate"
              value={`${result.dilution_ratio_water_to_concentrate} : 1`}
              sub="mass ratio"
              testId="beverage-ratio"
            />
            <Metric
              label="Estimated final mass"
              value={`${result.estimated_final_mass_kg} kg`}
              sub={`${result.final_volume_l} L at 1.00 kg/L`}
            />
          </div>
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-stone-700">
            <b className="text-amber-900">Advisory only:</b> Approximate formulation math does not establish shelf stability, a scheduled process, or process-authority approval.
          </div>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-[11px] leading-relaxed text-stone-600">
            {(result.advisory_notes || []).map((note) => <li key={note}>{note}</li>)}
          </ul>
        </>
      )}
    </Card>
  );
}

function BakersPercentCalculator() {
  const [flourWeightG, setFlourWeightG] = useState(1000);
  const [waterWeightG, setWaterWeightG] = useState(650);
  const [otherIngredientsG, setOtherIngredientsG] = useState(350);
  const [targetBatchWeightG, setTargetBatchWeightG] = useState(4000);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!flourWeightG || waterWeightG === "" || otherIngredientsG === "" || !targetBatchWeightG) {
      setResult(null);
      setError("");
      return;
    }
    const t = setTimeout(() => {
      api.post("/application/bakers-percent", {
        flour_weight_g: Number(flourWeightG),
        water_weight_g: Number(waterWeightG),
        other_ingredients_g: Number(otherIngredientsG),
        target_batch_weight_g: Number(targetBatchWeightG),
      })
        .then((r) => {
          setResult(r.data);
          setError("");
        })
        .catch((err) => {
          setResult(null);
          setError(err?.response?.data?.detail || "Check the formula and target-batch inputs.");
        });
    }, 250);
    return () => clearTimeout(t);
  }, [flourWeightG, waterWeightG, otherIngredientsG, targetBatchWeightG]);

  return (
    <Card className="p-5 border-orange-200" data-testid="bakers-percent-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-orange-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Baked goods · baker's percentage</div>
          <h3 className="font-semibold mt-0.5">Hydration &amp; pre-bake batch scaling</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <LabelField label="Total flour (g) · 100%">
          <Input
            type="number"
            min="0.01"
            step="1"
            value={flourWeightG}
            onChange={(e) => setFlourWeightG(e.target.value)}
            data-testid="bakers-flour"
          />
        </LabelField>
        <LabelField label="Formula water (g)">
          <Input
            type="number"
            min="0"
            step="1"
            value={waterWeightG}
            onChange={(e) => setWaterWeightG(e.target.value)}
            data-testid="bakers-water"
          />
        </LabelField>
        <LabelField label="Other ingredients (g)">
          <Input
            type="number"
            min="0"
            step="1"
            value={otherIngredientsG}
            onChange={(e) => setOtherIngredientsG(e.target.value)}
            data-testid="bakers-other"
          />
        </LabelField>
        <LabelField label="Target pre-bake batch (g)">
          <Input
            type="number"
            min="0.01"
            step="10"
            value={targetBatchWeightG}
            onChange={(e) => setTargetBatchWeightG(e.target.value)}
            data-testid="bakers-target"
          />
        </LabelField>
      </div>
      {error && (
        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" role="alert">
          {error}
        </div>
      )}
      {result && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="bakers-result">
            <Metric
              label="Hydration"
              value={`${result.hydration_pct}%`}
              sub="formula water ÷ flour"
              highlight
              testId="bakers-hydration"
            />
            <Metric
              label="Other ingredients"
              value={`${result.other_bakers_pct}%`}
              sub="relative to flour"
              testId="bakers-other-pct"
            />
            <Metric
              label="Total baker's %"
              value={`${result.total_bakers_pct}%`}
              sub={`${result.source_batch_weight_g} g source batch`}
              testId="bakers-total"
            />
            <Metric
              label="Scale factor"
              value={`${result.scale_factor}×`}
              sub={`${result.target_batch_weight_g} g pre-bake target`}
              testId="bakers-scale-factor"
            />
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3" data-testid="bakers-scaled-weights">
            <Metric label="Target flour" value={`${result.target_flour_g} g`} />
            <Metric label="Target water" value={`${result.target_water_g} g`} />
            <Metric label="Target other ingredients" value={`${result.target_other_ingredients_g} g`} />
          </div>
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-stone-700">
            <b className="text-amber-900">Yield boundary:</b> This is pre-bake formula mass. Record actual finished weight and bake loss; do not treat it as guaranteed packaged yield.
          </div>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-[11px] leading-relaxed text-stone-600">
            {(result.advisory_notes || []).map((note) => <li key={note}>{note}</li>)}
          </ul>
        </>
      )}
    </Card>
  );
}

function BakeryCalculatorTabs() {
  return (
    <Tabs defaultValue="bakers-percent" data-testid="bakery-calculator-tabs">
      <div className="mb-4 border-b border-stone-200">
        <TabsList
          aria-label="Bakery tools"
          className="h-auto rounded-none bg-transparent p-0"
        >
          <TabsTrigger
            value="bakers-percent"
            data-testid="bakers-percent-tab"
            className="rounded-none border-b-2 border-transparent px-1.5 py-2 text-xs text-stone-500 shadow-none data-[state=active]:border-orange-600 data-[state=active]:bg-transparent data-[state=active]:text-orange-800 data-[state=active]:shadow-none"
          >
            Baker&apos;s %
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent value="bakers-percent" className="mt-0">
        <BakersPercentCalculator />
      </TabsContent>
    </Tabs>
  );
}

export default function ApplicationCalculators({ productType = "dry_blend", browseAll = false }) {
  const [tool, setTool] = useState(browseAll ? "snack" : "recommended");
  const [showMeatYield, setShowMeatYield] = useState(false);
  // Show only the calculators relevant to this product type.
  // Dry blends → snack + dry-marinade application (the "how does my blend perform on X" tools)
  // Wet sauce → wet sauce reverse-math + shelf stability
  // Broth → soup/broth reverse-math + shelf stability
  // Wet marinade → wet sauce reverse-math (similar reverse math) + shelf stability
  const showSnack    = productType === "dry_blend";
  const showMarinade = productType === "dry_blend";
  const showWetSauce = productType === "wet_sauce" || productType === "wet_marinade" || productType === "dressing";
  const showBroth    = productType === "broth";
  const showBeverage = productType === "beverage";
  const showBakers   = productType === "baked_goods";
  const showShelf    = usesWetSavorySafetyTools(productType) || productType === "pet_topper";

  return (
    <>
      <div className="mb-4">
        <LabelField label="Choose an application calculator">
          <select className="w-full rounded-md border border-stone-300 bg-white p-3" value={tool} onChange={e=>setTool(e.target.value)} data-testid="application-tool-select">
            {!browseAll && <option value="recommended">Recommended for this formula</option>}
            <option value="snack">Snacks — slurry &amp; topical application</option>
            <option value="marinade">Meats &amp; proteins — marinade yield &amp; purge</option>
            <option value="sauce">Sauces, dressings &amp; wet marinades</option>
            <option value="broth">Soups &amp; broths</option>
            <option value="beverage">Beverages — batch scaling</option>
            <option value="bakery">Baked goods — baker's percentages &amp; hydration</option>
            <option value="shelf">Wet products — shelf-stability screening</option>
          </select>
        </LabelField>
        <p className="mt-2 text-xs text-stone-500">All tools remain available. Choosing a tool does not change your formula's category. These are application estimates, not measured results or safety approvals.</p>
      </div>
      {(tool === "snack" || (tool === "recommended" && showSnack)) && <SnackCalculator />}
      {tool === "recommended" && !showMarinade && <div className="mb-4 rounded-md border border-stone-200 p-4">
        <button type="button" className="font-semibold text-brand-800 underline" onClick={() => setShowMeatYield(v => !v)} data-testid="show-meat-yield">
          {showMeatYield ? "Hide" : "Open"} meat &amp; protein yield calculator
        </button>
        <p className="text-xs text-stone-500 mt-2">Standalone application estimates. Opening this tool does not change your product category or validate finished yield.</p>
      </div>}
      {(tool === "marinade" || (tool === "recommended" && (showMarinade || showMeatYield))) && <MarinadeCalculator />}
      {(tool === "sauce" || (tool === "recommended" && showWetSauce)) && <WetSauceCalculator />}
      {(tool === "broth" || (tool === "recommended" && showBroth)) && <SoupBrothCalculator />}
      {(tool === "beverage" || (tool === "recommended" && showBeverage)) && <BeverageBatchCalculator />}
      {(tool === "bakery" || (tool === "recommended" && showBakers)) && <BakeryCalculatorTabs />}
      {(tool === "shelf" || (tool === "recommended" && showShelf)) && <ShelfStabilityCalculator />}
    </>
  );
}

// ============================================================================
// WET SAUCE / DRESSING / GLAZE
// ============================================================================
function WetSauceCalculator() {
  const [sauceTypes, setSauceTypes] = useState([]);
  const [sauceKey, setSauceKey] = useState("bbq_sauce");
  const [volumeUnit, setVolumeUnit] = useState("gal");
  const [volume, setVolume] = useState(1);
  const [sodiumTarget, setSodiumTarget] = useState(280);
  const [blendSodium, setBlendSodium] = useState(146);
  const [blendSalt, setBlendSalt] = useState(38);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/application/sauce-types")
      .then((r) => setSauceTypes(r.data.sauce_types || []))
      .catch(() => {});
  }, []);

  // Auto-set defaults when sauce type changes
  useEffect(() => {
    const def = sauceTypes.find((s) => s.key === sauceKey);
    if (def) setSodiumTarget(def.default_sodium_per_serving_mg);
  }, [sauceKey, sauceTypes]);

  useEffect(() => {
    if (!sauceKey || !volume || !sodiumTarget || !blendSodium) {
      setResult(null);
      return;
    }
    const ml = volumeUnit === "gal" ? Number(volume) * GAL_TO_ML : Number(volume);
    const t = setTimeout(() => {
      api.post("/application/wet-sauce", {
        sauce_type: sauceKey,
        finished_volume_ml: ml,
        target_sodium_per_serving_mg: Number(sodiumTarget),
        blend_sodium_mg_per_g: Number(blendSodium),
        blend_salt_pct: Number(blendSalt),
      })
        .then((r) => setResult(r.data))
        .catch(() => setResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [sauceKey, volume, volumeUnit, sodiumTarget, blendSodium, blendSalt]);

  const preservationColor = {
    LOW: "bg-amber-50 border-amber-300 text-amber-900",
    OK: "bg-stone-50 border-stone-300 text-stone-800",
    GOOD: "bg-emerald-50 border-emerald-300 text-emerald-900",
    HIGH: "bg-rose-50 border-rose-300 text-rose-900",
  };

  return (
    <Card className="p-5 border-stone-200" data-testid="wet-sauce-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-brand-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Wet Sauce · reverse-math</div>
          <h3 className="font-semibold mt-0.5">Sauces, dressings, glazes &amp; salsas</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <LabelField label="Product type">
          <Select value={sauceKey} onValueChange={setSauceKey}>
            <SelectTrigger data-testid="sauce-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sauceTypes.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabelField>
        <LabelField label={`Finished volume (${volumeUnit})`}>
          <div className="flex gap-2">
            <Input type="number" step="0.1" min="0.1"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              data-testid="sauce-volume" className="flex-1" />
            <Select value={volumeUnit} onValueChange={(v) => {
              if (v === "ml" && volumeUnit === "gal") setVolume((Number(volume) * GAL_TO_ML).toFixed(0));
              if (v === "gal" && volumeUnit === "ml") setVolume((Number(volume) / GAL_TO_ML).toFixed(2));
              setVolumeUnit(v);
            }}>
              <SelectTrigger className="w-[80px]" data-testid="sauce-volume-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gal">gal</SelectItem>
                <SelectItem value="ml">mL</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </LabelField>
        <LabelField label="Target sodium / serving (mg)">
          <Input type="number" step="10" min="0"
            value={sodiumTarget}
            onChange={(e) => setSodiumTarget(e.target.value)}
            data-testid="sauce-sodium-target" />
        </LabelField>
        <LabelField label="Blend sodium (mg/g)">
          <Input type="number" step="1" min="0"
            value={blendSodium}
            onChange={(e) => setBlendSodium(e.target.value)}
            data-testid="sauce-blend-sodium" />
        </LabelField>
      </div>
      {result && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sauce-result">
            <Metric label="Dry blend dose"
              value={`${result.blend_g} g`} sub={`${result.blend_oz} oz · ${result.blend_lb} lb`} highlight testId="sauce-blend-dose" />
            <Metric label="% blend in sauce"
              value={`${result.blend_pct_of_sauce}%`} sub={`of ${result.finished_volume_gal} gal`} testId="sauce-blend-pct" />
            <Metric label="Salt % finished"
              value={`${result.salt_pct_finished}%`} sub={result.preservation_status} testId="sauce-salt-pct" />
            <Metric label="Servings"
              value={`${result.servings}`} sub={`${result.serving_size_g}g serving · ${result.sodium_per_serving_mg}mg Na`} testId="sauce-servings" />
          </div>
          <div className={`mt-3 p-3 rounded-md border text-sm ${preservationColor[result.preservation_status] || "bg-stone-50 border-stone-200"}`}
            data-testid="sauce-preservation-note">
            <span className="text-xs uppercase tracking-wider font-bold">Preservation · {result.preservation_status}</span>
            <div className="mt-1 text-[13px] leading-relaxed">{result.preservation_note}</div>
          </div>
          <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm" data-testid="sauce-shelf-route">
            <span className="text-xs uppercase tracking-wider text-amber-800 font-bold">Shelf route · {result.shelf_stability_route}</span>
            <div className="mt-1 text-[13px] text-stone-700 leading-relaxed">{result.shelf_stability_note}</div>
          </div>
          <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
            <b className="text-stone-700">Method:</b> {result.method}
          </div>
          <div className="mt-2 text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
            <b>Notes:</b> {result.notes}
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// SOUP / BROTH / STOCK
// ============================================================================
function SoupBrothCalculator() {
  const [brothTypes, setBrothTypes] = useState([]);
  const [brothKey, setBrothKey] = useState("chicken_stock");
  const [volumeUnit, setVolumeUnit] = useState("gal");
  const [volume, setVolume] = useState(1);
  const [sodiumTarget, setSodiumTarget] = useState(440);
  const [blendSodium, setBlendSodium] = useState(146);
  const [reduction, setReduction] = useState(20);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/application/broth-types")
      .then((r) => setBrothTypes(r.data.broth_types || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const def = brothTypes.find((b) => b.key === brothKey);
    if (def) {
      setSodiumTarget(def.default_sodium_per_cup_mg);
      setReduction(def.default_reduction_pct);
    }
  }, [brothKey, brothTypes]);

  useEffect(() => {
    if (!brothKey || !volume || !sodiumTarget || !blendSodium) {
      setResult(null);
      return;
    }
    const ml = volumeUnit === "gal" ? Number(volume) * GAL_TO_ML : Number(volume);
    const t = setTimeout(() => {
      api.post("/application/soup-broth", {
        broth_type: brothKey,
        finished_volume_ml: ml,
        target_sodium_per_cup_mg: Number(sodiumTarget),
        blend_sodium_mg_per_g: Number(blendSodium),
        reduction_pct: Number(reduction),
      })
        .then((r) => setResult(r.data))
        .catch(() => setResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [brothKey, volume, volumeUnit, sodiumTarget, blendSodium, reduction]);

  return (
    <Card className="p-5 border-stone-200" data-testid="soup-broth-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-brand-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Soup / Broth · reduction-aware</div>
          <h3 className="font-semibold mt-0.5">Stocks, broths, ramen base &amp; bouillon</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <LabelField label="Product type">
          <Select value={brothKey} onValueChange={setBrothKey}>
            <SelectTrigger data-testid="broth-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {brothTypes.map((b) => (
                <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabelField>
        <LabelField label={`Finished volume (${volumeUnit})`}>
          <div className="flex gap-2">
            <Input type="number" step="0.1" min="0.1"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              data-testid="broth-volume" className="flex-1" />
            <Select value={volumeUnit} onValueChange={(v) => {
              if (v === "ml" && volumeUnit === "gal") setVolume((Number(volume) * GAL_TO_ML).toFixed(0));
              if (v === "gal" && volumeUnit === "ml") setVolume((Number(volume) / GAL_TO_ML).toFixed(2));
              setVolumeUnit(v);
            }}>
              <SelectTrigger className="w-[80px]" data-testid="broth-volume-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gal">gal</SelectItem>
                <SelectItem value="ml">mL</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </LabelField>
        <LabelField label="Target sodium / cup (mg)">
          <Input type="number" step="10" min="0"
            value={sodiumTarget}
            onChange={(e) => setSodiumTarget(e.target.value)}
            data-testid="broth-sodium-target" />
        </LabelField>
        <LabelField label="Reduction during cook (%)">
          <Input type="number" step="1" min="0" max="60"
            value={reduction}
            onChange={(e) => setReduction(e.target.value)}
            data-testid="broth-reduction" />
        </LabelField>
      </div>
      {result && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="broth-result">
            <Metric label="Dry blend dose"
              value={`${result.blend_g} g`} sub={`${result.blend_oz} oz · ${result.blend_lb} lb`} highlight testId="broth-blend-dose" />
            <Metric label="Pre-reduction volume"
              value={`${result.pre_reduction_volume_gal} gal`} sub={`${result.pre_reduction_volume_ml} mL start`} testId="broth-prereduction" />
            <Metric label="Cups finished"
              value={`${result.cups_finished}`} sub={`@ ${result.sodium_per_cup_mg} mg Na/cup`} testId="broth-cups" />
            <Metric label="% blend in finished"
              value={`${result.blend_pct_of_finished}%`} sub="of finished broth weight" testId="broth-blend-pct" />
          </div>
          <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm" data-testid="broth-heat-note">
            <span className="text-xs uppercase tracking-wider text-amber-800 font-bold">Heat-stability strategy</span>
            <div className="mt-1 text-[13px] text-stone-700 leading-relaxed">{result.heat_stability_note}</div>
          </div>
          <div className="mt-3 text-[11px] text-stone-500 leading-relaxed">
            <b className="text-stone-700">Method:</b> {result.method}
          </div>
          <div className="mt-2 text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
            <b>Notes:</b> {result.notes}
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// SHELF STABILITY VALIDATOR (aw + pH + salt%)
// ============================================================================
function ShelfStabilityCalculator() {
  const [aw, setAw] = useState(0.92);
  const [ph, setPh] = useState(4.2);
  const [saltPct, setSaltPct] = useState(2.0);
  const [acidified, setAcidified] = useState(false);
  const [refrigerated, setRefrigerated] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!aw || !ph || saltPct === "" || saltPct === null) {
      setResult(null);
      return;
    }
    const t = setTimeout(() => {
      api.post("/application/shelf-stability", {
        aw: Number(aw),
        ph: Number(ph),
        salt_pct: Number(saltPct),
        acidified,
        refrigerated,
      })
        .then((r) => setResult(r.data))
        .catch(() => setResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [aw, ph, saltPct, acidified, refrigerated]);

  const statusBadge = {
    REVIEW_REQUIRED: "bg-amber-50 border-amber-400 text-amber-900",
    SHELF_STABLE: "bg-emerald-100 border-emerald-400 text-emerald-900",
    REFRIGERATION_REQUIRED: "bg-blue-100 border-blue-400 text-blue-900",
    UNSAFE_REFORMULATE: "bg-rose-100 border-rose-500 text-rose-900",
  };
  const flagBadge = {
    PASS: "bg-emerald-50 border-emerald-200 text-emerald-900",
    OK: "bg-stone-50 border-stone-200 text-stone-700",
    LOW: "bg-amber-50 border-amber-200 text-amber-900",
    CAUTION: "bg-amber-50 border-amber-300 text-amber-900",
    WARN: "bg-orange-50 border-orange-300 text-orange-900",
    HAZARD: "bg-rose-50 border-rose-300 text-rose-900",
  };

  return (
    <Card className="p-5 border-stone-200" data-testid="shelf-stability-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} className="text-brand-700" />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Shelf Stability · aw + pH + salt</div>
          <h3 className="font-semibold mt-0.5">Wet product hurdle review — not a safety validation</h3>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <LabelField label="Water activity (aw)">
          <Input type="number" step="0.01" min="0.01" max="1.0"
            value={aw}
            onChange={(e) => setAw(e.target.value)}
            data-testid="ss-aw" />
        </LabelField>
        <LabelField label="pH">
          <Input type="number" step="0.1" min="0" max="14"
            value={ph}
            onChange={(e) => setPh(e.target.value)}
            data-testid="ss-ph" />
        </LabelField>
        <LabelField label="Salt % (finished)">
          <Input type="number" step="0.1" min="0"
            value={saltPct}
            onChange={(e) => setSaltPct(e.target.value)}
            data-testid="ss-salt" />
        </LabelField>
        <LabelField label="Acidified?">
          <Select value={acidified ? "y" : "n"} onValueChange={(v) => setAcidified(v === "y")}>
            <SelectTrigger data-testid="ss-acidified"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="n">No</SelectItem>
              <SelectItem value="y">Yes (filed 21 CFR 114)</SelectItem>
            </SelectContent>
          </Select>
        </LabelField>
        <LabelField label="Refrigerated?">
          <Select value={refrigerated ? "y" : "n"} onValueChange={(v) => setRefrigerated(v === "y")}>
            <SelectTrigger data-testid="ss-refrigerated"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="n">No (ambient)</SelectItem>
              <SelectItem value="y">Yes (≤ 41°F)</SelectItem>
            </SelectContent>
          </Select>
        </LabelField>
      </div>
      {result && (
        <>
          <div className={`mt-4 p-4 rounded-md border-2 ${statusBadge[result.status] || "bg-stone-50 border-stone-300"}`}
            data-testid="ss-status">
            <div className="text-xs uppercase tracking-wider font-bold">Verdict · {result.status.replace(/_/g, " ")}</div>
            <div className="mt-1 text-[14px] leading-relaxed">{result.verdict}</div>
          </div>

          <div className="mt-3 space-y-2" data-testid="ss-flags">
            {result.hurdle_flags.map((f, i) => (
              <div key={i} className={`p-2.5 rounded border text-[12px] ${flagBadge[f.level] || "bg-stone-50 border-stone-200"}`}>
                <span className="text-[10px] uppercase tracking-wider font-bold mr-2">{f.level}</span>
                {f.note}
              </div>
            ))}
          </div>

          {result.label_requirements?.length > 0 && (
            <div className="mt-3 p-3 rounded-md bg-stone-900 text-stone-50 text-[12px]" data-testid="ss-label-reqs">
              <div className="text-[10px] uppercase tracking-wider font-bold mb-1.5 text-stone-300">Required label statements</div>
              <ul className="list-disc pl-4 space-y-1">
                {result.label_requirements.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {result.cfr_references?.length > 0 && (
            <div className="mt-2 text-[11px] text-stone-600 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
              <b>21 CFR refs:</b>
              <ul className="list-disc pl-4 mt-1 space-y-0.5">
                {result.cfr_references.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
